import { supabase } from '../lib/supabase';

/**
 * Service for managing prompt templates
 * Handles snake_case (database) to camelCase (React) conversion
 */
export const promptTemplateService = {
  /**
   * Get prompt template for a specific use case and AI tool
   * @param {string} useCaseId - UUID of the use case
   * @param {string} aiToolId - UUID of the AI tool
   * @returns {Promise<Object|null>} Prompt template or null if not found
   */
  async getPromptTemplate(useCaseId, aiToolId) {
    const { data, error } = await supabase
      ?.from('prompt_templates')
      ?.select('*')
      ?.eq('use_case_id', useCaseId)
      ?.eq('ai_tool_id', aiToolId)
      ?.single();
    
    if (error) {
      if (error?.code === 'PGRST116') {
        // No rows returned
        return null;
      }
      throw error;
    }
    
    return {
      id: data?.id,
      useCaseId: data?.use_case_id,
      aiToolId: data?.ai_tool_id,
      templateText: data?.template_text,
      createdAt: data?.created_at,
      updatedAt: data?.updated_at
    };
  },

  /**
   * Create a new prompt template
   * @param {Object} template - Template data in camelCase
   * @returns {Promise<Object>} Created prompt template
   */
  async createPromptTemplate(template) {
    const { data, error } = await supabase
      ?.from('prompt_templates')
      ?.insert({
        use_case_id: template?.useCaseId,
        ai_tool_id: template?.aiToolId,
        template_text: template?.templateText
      })
      ?.select()
      ?.single();
    
    if (error) throw error;
    
    return {
      id: data?.id,
      useCaseId: data?.use_case_id,
      aiToolId: data?.ai_tool_id,
      templateText: data?.template_text,
      createdAt: data?.created_at,
      updatedAt: data?.updated_at
    };
  },

  /**
   * Update an existing prompt template
   * @param {string} id - UUID of the prompt template
   * @param {Object} updates - Updated fields in camelCase
   * @returns {Promise<Object>} Updated prompt template
   */
  async updatePromptTemplate(id, updates) {
    const dbUpdates = {};
    if (updates?.templateText !== undefined) dbUpdates.template_text = updates?.templateText;
    dbUpdates.updated_at = new Date()?.toISOString();
    
    const { data, error } = await supabase
      ?.from('prompt_templates')
      ?.update(dbUpdates)
      ?.eq('id', id)
      ?.select()
      ?.single();
    
    if (error) throw error;
    
    return {
      id: data?.id,
      useCaseId: data?.use_case_id,
      aiToolId: data?.ai_tool_id,
      templateText: data?.template_text,
      createdAt: data?.created_at,
      updatedAt: data?.updated_at
    };
  },

  /**
   * Upsert (create or update) a prompt template
   * @param {Object} template - Template data in camelCase
   * @returns {Promise<Object>} Created or updated prompt template
   */
  async upsertPromptTemplate(template) {
    const { data, error } = await supabase
      ?.from('prompt_templates')
      ?.upsert({
        use_case_id: template?.useCaseId,
        ai_tool_id: template?.aiToolId,
        template_text: template?.templateText,
        updated_at: new Date()?.toISOString()
      }, {
        onConflict: 'use_case_id,ai_tool_id'
      })
      ?.select()
      ?.single();
    
    if (error) throw error;
    
    return {
      id: data?.id,
      useCaseId: data?.use_case_id,
      aiToolId: data?.ai_tool_id,
      templateText: data?.template_text,
      createdAt: data?.created_at,
      updatedAt: data?.updated_at
    };
  },

  /**
   * Delete a prompt template
   * @param {string} id - UUID of the prompt template
   * @returns {Promise<void>}
   */
  async deletePromptTemplate(id) {
    const { error } = await supabase
      ?.from('prompt_templates')
      ?.delete()
      ?.eq('id', id);
    
    if (error) throw error;
  }
};